import type { AudioSource, BannerPosition, BannerTimingMode, ClipBackgroundMode, FolderCategory, FilmStatus, ModerationStatus, SubtitlePosition } from '@content-engine/shared';
import { request } from './api.ts';
import type { Account, AuditEvent, AuditFilters, Banner, BannerKind, BannerPurpose, BannerStatus, Campaign, Film, ManagedUser, Moment, MomentStrategy, Pagination, PostDraft, QuarantineResponse, ScanResult, Transcript } from './types.ts';

// Campaigns
export const fetchCampaigns = (): Promise<{ campaigns: Campaign[] }> =>
  request<{ campaigns: Campaign[] }>('/campaigns');

export const fetchCampaign = (id: string): Promise<{ campaign: Campaign }> =>
  request<{ campaign: Campaign }>(`/campaigns/${id}`);

export const updateCampaign = (
  id: string,
  patch: Partial<Pick<Campaign, 'name' | 'vertical' | 'keywords' | 'status' | 'maxPostsPerDay' | 'cronSchedule' | 'captionPromptTemplate' | 'bannerIds'>>,
): Promise<{ campaign: Campaign }> =>
  request<{ campaign: Campaign }>(`/campaigns/${id}`, { method: 'PATCH', body: patch });

export const deleteCampaign = (id: string): Promise<void> =>
  request<void>(`/campaigns/${id}`, { method: 'DELETE' });

export interface ScrapeTikTokResult {
  runId: string;
  raw: number;
  cheapKept: number;
  cheapReasons: Record<string, number>;
  claudeUsed: boolean;
  claudeKept: number;
  claudeRationale: string[] | null;
  finalDrafts: number;
}

export const scrapeTikTok = (
  campaignId: string,
  body?: {
    searchQueries?: string[];
    resultsPerPage?: number;
    useClaudeFilter?: boolean;
    claudeMaxKeep?: number;
  },
): Promise<ScrapeTikTokResult> =>
  request<ScrapeTikTokResult>(`/campaigns/${campaignId}/scrape-tiktok`, {
    method: 'POST',
    body: body ?? {},
  });

export const createIdeaDraft = (
  campaignId: string,
  topic: string,
): Promise<{ draft: PostDraft }> =>
  request<{ draft: PostDraft }>(`/campaigns/${campaignId}/idea-draft`, {
    method: 'POST',
    body: { topic },
  });

// Accounts
export const fetchAccounts = (filters?: {
  platform?: string;
  status?: string;
}): Promise<{ accounts: Account[] }> => {
  const params = new URLSearchParams();
  if (filters?.platform) params.set('platform', filters.platform);
  if (filters?.status) params.set('status', filters.status);
  const qs = params.toString();
  return request<{ accounts: Account[] }>(`/accounts${qs ? `?${qs}` : ''}`);
};

export const createAccount = (
  payload: Pick<Account, 'platform' | 'handle'> &
    Partial<Pick<Account, 'displayName' | 'status' | 'proxyUrl' | 'maxPostsPerDay' | 'minMinutesBetweenPosts' | 'notes'>>,
): Promise<{ account: Account }> =>
  request<{ account: Account }>('/accounts', { method: 'POST', body: payload });

export const updateAccount = (
  id: string,
  patch: Partial<Account>,
): Promise<{ account: Account }> =>
  request<{ account: Account }>(`/accounts/${id}`, { method: 'PATCH', body: patch });

export const deleteAccount = (id: string): Promise<void> =>
  request<void>(`/accounts/${id}`, { method: 'DELETE' });

// Posts (after approve, in pipeline / published)
export const fetchPosts = (filters?: {
  status?: 'scheduled' | 'publishing' | 'published' | 'failed' | 'cancelled';
  accountId?: string;
  limit?: number;
  offset?: number;
}): Promise<{ posts: import('./types.ts').Post[]; pagination: Pagination }> => {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.accountId) params.set('accountId', filters.accountId);
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return request<{ posts: import('./types.ts').Post[]; pagination: Pagination }>(
    `/posts${qs ? `?${qs}` : ''}`,
  );
};

export const fetchPostDetail = (
  id: string,
): Promise<{ post: import('./types.ts').PostDetail }> =>
  request<{ post: import('./types.ts').PostDetail }>(`/posts/${id}`);

// Post drafts
export const fetchDrafts = (filters?: {
  moderationStatus?: ModerationStatus;
  campaignId?: string;
  limit?: number;
  offset?: number;
}): Promise<{ drafts: PostDraft[]; pagination: Pagination }> => {
  const params = new URLSearchParams();
  if (filters?.moderationStatus) params.set('moderationStatus', filters.moderationStatus);
  if (filters?.campaignId) params.set('campaignId', filters.campaignId);
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return request<{ drafts: PostDraft[]; pagination: Pagination }>(
    `/post-drafts${qs ? `?${qs}` : ''}`,
  );
};

export const approveDraft = (
  id: string,
  payload: { accountIds: string[]; scheduledFor?: string; moderationNotes?: string | null },
): Promise<{ posts: { id: string }[] }> =>
  request<{ posts: { id: string }[] }>(`/post-drafts/${id}/approve`, {
    method: 'POST',
    body: payload,
  });

export const rejectDraft = (
  id: string,
  moderationNotes?: string | null,
): Promise<{ draft: PostDraft }> =>
  request<{ draft: PostDraft }>(`/post-drafts/${id}/reject`, {
    method: 'POST',
    body: { moderationNotes: moderationNotes ?? null },
  });

export const generateDraftScript = (
  id: string,
  body?: {
    language?: string;
    targetDurationSec?: number;
    moderatorHint?: string | null;
  },
): Promise<{ draft: PostDraft }> =>
  request<{ draft: PostDraft }>(`/post-drafts/${id}/generate-script`, {
    method: 'POST',
    body: body ?? {},
  });

export const generateDraftVideo = (
  id: string,
  body?: {
    prompt?: string;
    durationSec?: number;
    aspectRatio?: '9:16' | '1:1' | '16:9';
    model?: string;
  },
): Promise<{ draft: PostDraft; videoUrl: string | null }> =>
  request<{ draft: PostDraft; videoUrl: string | null }>(
    `/post-drafts/${id}/generate-video`,
    { method: 'POST', body: body ?? {} },
  );

// Films
export interface FilmsListResponse {
  films: import('./types.ts').FilmListItem[];
  counts?: Record<string, import('./types.ts').FilmChildCounts>;
}

export const fetchFilms = (filters?: {
  status?: FilmStatus;
  category?: FolderCategory;
  createdBy?: 'me' | 'all' | string;
  limit?: number;
  offset?: number;
  include?: 'counts';
  /** Архивные фильмы скрыты, пока чекбокс «Показать архивные» не включён. */
  showArchived?: boolean;
}): Promise<FilmsListResponse> => {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.category) params.set('category', filters.category);
  if (filters?.createdBy) params.set('createdBy', filters.createdBy);
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
  if (filters?.include) params.set('include', filters.include);
  if (filters?.showArchived) params.set('showArchived', 'true');
  const qs = params.toString();
  return request<FilmsListResponse>(`/films${qs ? `?${qs}` : ''}`);
};

export const fetchFilm = (id: string): Promise<import('./types.ts').FilmDetailResponse> =>
  request<import('./types.ts').FilmDetailResponse>(`/films/${id}`);

export const scanFilms = (body: {
  path: string;
  folderCategory: FolderCategory;
  dryRun?: boolean;
}): Promise<ScanResult> =>
  request<ScanResult>('/films/scan', { method: 'POST', body });

export const registerFilm = (body: {
  path: string;
  folderCategory: FolderCategory;
}): Promise<{ film: Film }> =>
  request<{ film: Film }>('/films', { method: 'POST', body });

export const updateFilm = (
  id: string,
  patch: Partial<Pick<Film, 'maxClipCount' | 'clipDurationMinSec' | 'clipDurationMaxSec' | 'parsedTitle' | 'parsedYear'>>,
): Promise<{ film: Film }> =>
  request<{ film: Film }>(`/films/${id}`, { method: 'PATCH', body: patch });

// DELETE /films/:id больше не зовётся из UI: пользователь архивирует
// (archiveEntity('film', …)), а удаление — админский контур (требует reason).

export const initFilmUpload = (
  body: { filename: string; contentType?: string },
): Promise<{ filmId: string; key: string; putUrl: string }> =>
  request<{ filmId: string; key: string; putUrl: string }>('/films/upload/init', { method: 'POST', body });

export const completeFilmUpload = (
  body: { filmId: string; sizeBytes: number },
): Promise<{ filmId: string }> =>
  request<{ filmId: string }>('/films/upload/complete', { method: 'POST', body });

/** Raw PUT straight to R2 — NOT through request() (different origin, no auth header, body is the File). */
export const uploadFilmFileToR2 = async (putUrl: string, file: File): Promise<void> => {
  const res = await fetch(putUrl, { method: 'PUT', body: file, headers: file.type ? { 'Content-Type': file.type } : undefined });
  if (!res.ok) throw new Error(`R2 upload failed: ${res.status}`);
};

export const filmFromUrl = (body: { url: string }): Promise<{ filmId: string; jobId: string }> =>
  request<{ filmId: string; jobId: string }>('/films/from-url', { method: 'POST', body });

export const filmFromR2Key = (body: { key: string; name?: string }): Promise<{ filmId: string }> =>
  request<{ filmId: string }>('/films/from-r2-key', { method: 'POST', body });

export interface ScrapeResponse {
  platform: string;
  rawCount: number;
  filtered: number;
  filterReasons: Record<string, number>;
  enqueuedCount: number;
  films: Array<{ filmId: string; jobId: string; sourceUrl: string; authorHandle: string | null; viewCount: number | null }>;
}

export const scrapeFilms = (body: {
  platform: 'tiktok' | 'instagram' | 'youtube' | 'twitter' | 'vk';
  queries: string[];
  topN?: number;
  minPlayCount?: number;
  minDurationSec?: number;
  maxDurationSec?: number;
}): Promise<ScrapeResponse> =>
  request<ScrapeResponse>('/films/scrape', { method: 'POST', body });

export const deleteFilmSource = (id: string): Promise<void> =>
  request<void>(`/films/${id}/source`, { method: 'DELETE' });

export const fetchTranscript = (filmId: string): Promise<{ transcript: Transcript }> =>
  request<{ transcript: Transcript }>(`/films/${filmId}/transcript`);

export const transcribeFilm = (filmId: string): Promise<{ jobId: string }> =>
  request<{ jobId: string }>(`/films/${filmId}/transcribe`, { method: 'POST' });

// Moments
export const fetchMoments = (
  filmId: string,
  filters?: { strategy?: MomentStrategy; showArchived?: boolean },
): Promise<{ moments: Moment[] }> => {
  const params = new URLSearchParams();
  if (filters?.strategy) params.set('strategy', filters.strategy);
  // Без этого архивный момент, у которого клип ещё не нарезан, было бы
  // невозможно достать из UI: в /clips его тоже нет — клипа не существует.
  if (filters?.showArchived) params.set('showArchived', 'true');
  const qs = params.toString();
  return request<{ moments: Moment[] }>(
    `/films/${filmId}/moments${qs ? `?${qs}` : ''}`,
  );
};

export const selectMomentsApi = (
  filmId: string,
  body: {
    strategy: MomentStrategy;
    count?: number;
    durMinSec?: number;
    durMaxSec?: number;
    customPrompt?: string;
  },
): Promise<{ jobId: string }> =>
  request<{ jobId: string }>(`/films/${filmId}/select-moments`, {
    method: 'POST',
    body,
  });

export const updateMomentApi = (
  id: string,
  patch: { startSec?: number; endSec?: number; title?: string },
): Promise<{ moment: Moment }> =>
  request<{ moment: Moment }>(`/moments/${id}`, { method: 'PATCH', body: patch });

export const createManualMoment = (
  filmId: string,
  body: { startSec: number; endSec: number; title?: string },
): Promise<{ moment: Moment }> =>
  request<{ moment: Moment }>(`/films/${filmId}/moments/manual`, { method: 'POST', body });

export const transcribeMomentApi = (momentId: string): Promise<{ jobId: string }> =>
  request<{ jobId: string }>(`/moments/${momentId}/transcribe`, { method: 'POST' });

// Clips
import type {
  ArchiveCounts,
  Clip,
  ClipDetailResponse,
  ClipListItem,
  ClipStatus,
  ClipVariant,
  ClipVariantStatus,
  FilmClipsParams,
  FilmClipsResponse,
  VariantDetailResponse,
  VariantsListResponse,
} from './types.ts';

export const generateClipForMoment = (
  momentId: string,
  body?: {
    subtitleLanguage?: 'source' | 'ru' | 'en';
  },
): Promise<{ clipId: string; jobId: string }> =>
  request<{ clipId: string; jobId: string }>(`/moments/${momentId}/generate-clip`, {
    method: 'POST',
    body: body ?? {},
  });

export const bulkGenerateClips = (
  filmId: string,
  body: {
    momentIds?: string[];
    strategy?: string;
    subtitleLanguage?: 'source' | 'ru' | 'en';
  },
): Promise<{ jobIds: Array<{ momentId: string; clipId: string; jobId: string }> }> =>
  request<{ jobIds: Array<{ momentId: string; clipId: string; jobId: string }> }>(
    `/films/${filmId}/clips/generate`,
    { method: 'POST', body },
  );

export const fetchFilmClips = (
  filmId: string,
  params: FilmClipsParams = {},
): Promise<FilmClipsResponse> => {
  const sp = new URLSearchParams();
  if (params.limit != null) sp.set('limit', String(params.limit));
  if (params.offset != null) sp.set('offset', String(params.offset));
  for (const s of params.status ?? []) sp.append('status', s);
  if (params.include) sp.set('include', params.include);
  if (params.counts) sp.set('counts', 'true');
  if (params.showArchived) sp.set('showArchived', 'true');
  const qs = sp.toString();
  return request<FilmClipsResponse>(`/films/${filmId}/clips${qs ? `?${qs}` : ''}`);
};

export interface ClipsListResponse {
  clips: ClipListItem[];
  total: number;
  counts?: Record<ClipStatus, number> & ArchiveCounts;
  variants?: Record<string, ClipVariant[]>;
}

export const fetchClips = (filters?: {
  status?: ClipStatus[];
  filmId?: string;
  createdBy?: 'me' | 'all' | string;
  limit?: number;
  offset?: number;
  include?: 'variants';
  counts?: boolean;
  /** Архивные скрыты, пока чекбокс «Показать архивные» не включён. */
  showArchived?: boolean;
  /** Поиск по названию момента — на сервере, ДО пагинации (ILIKE, регистронезависимо). */
  search?: string;
}): Promise<ClipsListResponse> => {
  const params = new URLSearchParams();
  for (const s of filters?.status ?? []) params.append('status', s);
  if (filters?.filmId) params.set('filmId', filters.filmId);
  if (filters?.createdBy) params.set('createdBy', filters.createdBy);
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
  if (filters?.include) params.set('include', filters.include);
  if (filters?.counts) params.set('counts', 'true');
  if (filters?.showArchived) params.set('showArchived', 'true');
  if (filters?.search?.trim()) params.set('search', filters.search.trim());
  const qs = params.toString();
  return request<ClipsListResponse>(`/clips${qs ? `?${qs}` : ''}`);
};

export const fetchClip = (id: string): Promise<ClipDetailResponse> =>
  request<ClipDetailResponse>(`/clips/${id}`);

export const updateClipApi = (
  id: string,
  patch: { status?: 'approved' | 'rejected'; moderatorNotes?: string | null; rejectedReason?: string | null },
): Promise<{ clip: Clip }> =>
  request<{ clip: Clip }>(`/clips/${id}`, { method: 'PATCH', body: patch });

export const regenerateClipApi = (
  id: string,
  body?: Record<string, never>,
): Promise<{ jobId: string }> =>
  request<{ jobId: string }>(`/clips/${id}/regenerate`, {
    method: 'POST',
    body: body ?? {},
  });

// Clip variants + Telegram share (Phase 2.5)
export interface CreateVariantParams {
  bannerLibraryId?: string | null;
  position?: BannerPosition;
  sizePct?: number;
  startSec?: number;
  endSec?: number;
  loop?: boolean;
  chromaKeyColor?: string;
  chromaSimilarity?: number;
  chromaBlend?: number;
  disableChromaKey?: boolean;
  cropTopPct?: number;
  cropRightPct?: number;
  cropBottomPct?: number;
  cropLeftPct?: number;
  positionXPct?: number;
  positionYPct?: number;
  bannerTimingMode?: BannerTimingMode;
  freezeAtSec?: number;
  freezeDurationSec?: number;
  audioSource?: AudioSource;
  backgroundMode?: ClipBackgroundMode;
  backgroundBannerId?: string;
  subtitlePosition?: SubtitlePosition;
  burnSubtitles?: boolean;
}

export const createVariantApi = (
  clipId: string,
  params: CreateVariantParams,
): Promise<{ variantId: string; jobId: string }> =>
  request<{ variantId: string; jobId: string }>(`/clips/${clipId}/variants`, {
    method: 'POST',
    body: params,
  });

export const fetchClipVariants = (clipId: string): Promise<{ variants: ClipVariant[] }> =>
  request<{ variants: ClipVariant[] }>(`/clips/${clipId}/variants`);

/** Плоский список вариантов — вариант это конечный продукт, он живёт своей
 *  жизнью независимо от архивности клипа и фильма. */
export const fetchVariants = (filters?: {
  status?: ClipVariantStatus[];
  bannerId?: string;
  limit?: number;
  offset?: number;
  showArchived?: boolean;
}): Promise<VariantsListResponse> => {
  const params = new URLSearchParams();
  for (const s of filters?.status ?? []) params.append('status', s);
  if (filters?.bannerId) params.set('bannerId', filters.bannerId);
  if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
  if (filters?.showArchived) params.set('showArchived', 'true');
  const qs = params.toString();
  return request<VariantsListResponse>(`/variants${qs ? `?${qs}` : ''}`);
};

export const fetchVariant = (variantId: string): Promise<VariantDetailResponse> =>
  request<VariantDetailResponse>(`/clips/variants/${variantId}`);

export const updateVariantApi = (
  variantId: string,
  patch: { status?: 'approved' | 'rejected'; moderatorNotes?: string | null; rejectedReason?: string | null },
): Promise<{ variant: ClipVariant }> =>
  request<{ variant: ClipVariant }>(`/clips/variants/${variantId}`, { method: 'PATCH', body: patch });

export const regenerateVariantApi = (
  variantId: string,
  body?: {
    backgroundMode?: ClipBackgroundMode;
    backgroundBannerId?: string | null;
    subtitlePosition?: SubtitlePosition;
    burnSubtitles?: boolean;
  },
): Promise<{ jobId: string }> =>
  request<{ jobId: string }>(`/clips/variants/${variantId}/regenerate`, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });

export const shareTelegramApi = (
  clipId: string,
  body: { accountId: string; variantId?: string },
): Promise<{ messageId?: number | string | null; chatId?: string }> =>
  request<{ messageId?: number | string | null; chatId?: string }>(
    `/clips/${clipId}/share/telegram`,
    { method: 'POST', body },
  );

// Banner library (Phase 2.5)
export interface BannerUploadParams {
  name: string;
  tags?: string[];
  purpose?: BannerPurpose;
  defaultPosition?: BannerPosition;
  defaultSizePct?: number;
  defaultChromaColor?: string;
  defaultChromaSimilarity?: number;
  defaultChromaBlend?: number;
  defaultLoop?: boolean;
  defaultCropTopPct?: number;
  defaultCropRightPct?: number;
  defaultCropBottomPct?: number;
  defaultCropLeftPct?: number;
  defaultAudioSource?: AudioSource;
  notes?: string;
}

export const uploadBannerApi = (file: File, params: BannerUploadParams): Promise<{ banner: Banner }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('params', JSON.stringify(params));
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
  return fetch(`${apiBase}/banners`, {
    method: 'POST',
    credentials: 'include',
    headers: { Authorization: `Bearer ${localStorage.getItem('ce_access_token') ?? ''}` },
    body: formData,
  }).then(async (r) => {
    if (!r.ok) {
      const text = await r.text();
      throw new Error(`Banner upload failed: ${r.status} ${text}`);
    }
    return r.json() as Promise<{ banner: Banner }>;
  });
};

export const uploadBannerFromUrlApi = (
  url: string,
  params: BannerUploadParams,
): Promise<{ banner: Banner }> =>
  request<{ banner: Banner }>('/banners/from-url', {
    method: 'POST',
    body: { url, params },
  });

export const fetchBanners = (filters?: {
  status?: BannerStatus | 'all';
  kind?: BannerKind;
  purpose?: BannerPurpose;
  tag?: string;
}): Promise<{ banners: Banner[] }> => {
  const params = new URLSearchParams();
  if (filters?.status) params.set('status', filters.status);
  if (filters?.kind) params.set('kind', filters.kind);
  if (filters?.purpose) params.set('purpose', filters.purpose);
  if (filters?.tag) params.set('tag', filters.tag);
  const qs = params.toString();
  return request<{ banners: Banner[] }>(`/banners${qs ? `?${qs}` : ''}`);
};

export const fetchBanner = (id: string): Promise<{ banner: Banner; presignedUrl: string | null }> =>
  request<{ banner: Banner; presignedUrl: string | null }>(`/banners/${id}`);

export const updateBannerApi = (
  id: string,
  patch: Partial<{
    name: string;
    tags: string[];
    purpose: BannerPurpose;
    defaultPosition: BannerPosition;
    defaultSizePct: number;
    defaultChromaColor: string | null;
    defaultChromaSimilarity: number | null;
    defaultChromaBlend: number | null;
    defaultLoop: boolean;
    defaultCropTopPct: number;
    defaultCropRightPct: number;
    defaultCropBottomPct: number;
    defaultCropLeftPct: number;
    defaultAudioSource: AudioSource;
    notes: string | null;
    status: BannerStatus;
  }>,
): Promise<{ banner: Banner }> =>
  request<{ banner: Banner }>(`/banners/${id}`, { method: 'PATCH', body: patch });

// Campaign banner upload
export const uploadCampaignBanner = async (
  campaignId: string,
  file: File,
): Promise<{ campaign: Campaign; bannerR2Key: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  const apiBase = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api';
  const response = await fetch(`${apiBase}/campaigns/${campaignId}/banner`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('ce_access_token') ?? ''}`,
    },
    body: formData,
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Upload failed: ${response.status} ${text}`);
  }
  return response.json() as Promise<{ campaign: Campaign; bannerR2Key: string }>;
};

// Users (admin-only)
export const fetchUsers = (): Promise<{ users: ManagedUser[] }> =>
  request<{ users: ManagedUser[] }>('/auth/users');

export const createUser = (body: {
  email: string;
  password: string;
  role: 'admin' | 'moderator';
}): Promise<{ user: ManagedUser }> =>
  request<{ user: ManagedUser }>('/auth/users', { method: 'POST', body });

export const updateUser = (
  id: string,
  body: { role?: 'admin' | 'moderator'; password?: string },
): Promise<{ user: ManagedUser }> =>
  request<{ user: ManagedUser }>(`/auth/users/${id}`, { method: 'PATCH', body });

export const deleteUser = (id: string): Promise<void> =>
  request<void>(`/auth/users/${id}`, { method: 'DELETE' });

// Audit + Quarantine (admin-only)
export const fetchAuditEvents = (filters: AuditFilters = {}): Promise<{ events: AuditEvent[] }> => {
  const qs = new URLSearchParams(
    Object.entries(filters).filter(([, v]) => v != null && v !== '') as [string, string][],
  ).toString();
  return request<{ events: AuditEvent[] }>(`/admin/audit${qs ? `?${qs}` : ''}`);
};

export const fetchQuarantine = (type?: string): Promise<QuarantineResponse> =>
  request<QuarantineResponse>(`/admin/quarantine${type ? `?type=${type}` : ''}`);

export const restoreQuarantined = (type: string, id: string): Promise<{ restored: boolean }> =>
  request<{ restored: boolean }>(`/admin/quarantine/${type}/${id}/restore`, { method: 'POST' });

export const purgeQuarantined = (type: string, id: string): Promise<{ purged: boolean }> =>
  request<{ purged: boolean }>(`/admin/quarantine/${type}/${id}/purge`, { method: 'POST' });

// Archive — пользовательская замена удаления (спека §2). Никаких каскадов:
// архивируется ровно одна запись (момент и клип — одна сущность, их пара).
export type ArchiveEntityType = 'film' | 'moment' | 'clip' | 'clip_variant';

/** freeFiles=true планирует освобождение файлов через retention-срок роли. */
export const archiveEntity = (
  type: ArchiveEntityType,
  id: string,
  opts?: { freeFiles?: boolean },
): Promise<{ archived: boolean }> =>
  request<{ archived: boolean }>(`/archive/${type}/${id}`, {
    method: 'POST',
    body: { freeFiles: opts?.freeFiles ?? false },
  });

export const unarchiveEntity = (
  type: ArchiveEntityType,
  id: string,
): Promise<{ restored: boolean }> =>
  request<{ restored: boolean }>(`/archive/${type}/${id}/restore`, { method: 'POST' });

// ── Admin: background job queues ─────────────────────────────────────
export const fetchQueues = (): Promise<import('./types.ts').QueuesResponse> =>
  request<import('./types.ts').QueuesResponse>('/admin/queues');
